/**
 * 多语言 SEO 配置
 * 支持: en (英语), vn (越南语), pl (波兰语), it (意大利语)
 */

export interface PageSEO {
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
}

export interface LocaleSEO {
  home: PageSEO;
  about: PageSEO;
  contact: PageSEO;
  coreOffers: PageSEO;
  sourceProduct: PageSEO;
  // 产品分类
  fans: PageSEO;
  beltConveyor: PageSEO;
  dustRemoval: PageSEO;
  filtration: PageSEO;
  flowComponents: PageSEO;
  silosTanks: PageSEO;
  steelStructures: PageSEO;
}

export const seoConfig: Record<string, LocaleSEO> = {
  // 英语 SEO
  en: {
    home: {
      title: 'Creston Group | Industrial Sourcing & Engineering Solutions from China',
      description: 'Creston Group provides industrial equipment sourcing, engineering support, and logistics solutions from China. Your trusted partner for cost-effective manufacturing.',
      keywords: 'China sourcing, industrial equipment, engineering solutions, manufacturing China, Creston Group',
    },
    about: {
      title: 'About Us | Creston Group - Sino-American Joint Venture',
      description: 'Learn about Creston Group, a Sino-American joint venture in Shanghai specializing in engineering solutions and industrial sourcing services.',
      keywords: 'Creston Group, about us, Shanghai company, Sino-American joint venture, industrial sourcing',
    },
    contact: {
      title: 'Contact Us | Creston Group - Get in Touch',
      description: 'Contact Creston Group for industrial sourcing inquiries. Our team in Shanghai is ready to help with your equipment and engineering needs.',
      keywords: 'contact Creston, industrial sourcing inquiry, Shanghai contact, equipment supplier',
    },
    coreOffers: {
      title: 'Our Services | Sourcing, Engineering & Logistics | Creston Group',
      description: 'Explore Creston Group services: equipment sourcing from China, engineering support, aftermarket services, and integrated logistics solutions.',
      keywords: 'sourcing services, engineering support, logistics China, aftermarket services, industrial solutions',
    },
    sourceProduct: {
      title: 'Source Products from China | Industrial Equipment | Creston Group',
      description: 'Source industrial equipment from China: fans, conveyors, dust removal systems, silos, and more. Quality products at competitive prices.',
      keywords: 'source products China, industrial equipment, fans, conveyors, dust collectors, silos',
    },
    fans: {
      title: 'Industrial Fans from China | Centrifugal, Axial & Blower | Creston Group',
      description: 'Source industrial fans from China: centrifugal, axial, and blower fans for ventilation systems. Competitive prices with engineering support.',
      keywords: 'industrial fans China, centrifugal fan, axial fan, blower fan, ventilation system',
    },
    beltConveyor: {
      title: 'Belt Conveyor Systems from China | Industrial Conveyors | Creston Group',
      description: 'Source belt conveyor systems from China for mining, manufacturing, and logistics. Custom designs available.',
      keywords: 'belt conveyor China, industrial conveyor, material handling, mining conveyor',
    },
    dustRemoval: {
      title: 'Dust Removal Systems from China | Industrial Dust Collectors | Creston Group',
      description: 'Source dust removal systems from China: bag filters, cyclones, and electrostatic precipitators for industrial applications.',
      keywords: 'dust removal China, dust collector, bag filter, cyclone separator, air pollution control',
    },
    filtration: {
      title: 'Filtration Accessories from China | Filter Bags & Cages | Creston Group',
      description: 'Source filtration accessories from China: filter bags, cages, and media for dust collection systems.',
      keywords: 'filter bags China, filter cages, filtration media, dust collector parts',
    },
    flowComponents: {
      title: 'Flow Components from China | Valves & Gates | Creston Group',
      description: 'Source flow components from China: rotary valves, slide gates, and diverter valves for bulk material handling.',
      keywords: 'rotary valve China, slide gate, diverter valve, flow control, bulk material handling',
    },
    silosTanks: {
      title: 'Silos & Tanks from China | Industrial Storage | Creston Group',
      description: 'Source industrial silos and tanks from China for cement, grain, and bulk material storage.',
      keywords: 'steel silo China, storage tank, industrial hopper, bulk storage, cement silo',
    },
    steelStructures: {
      title: 'Steel Structures from China | Industrial Fabrication | Creston Group',
      description: 'Source steel structures from China: frames, platforms, walkways, and custom metal fabrication.',
      keywords: 'steel structure China, metal fabrication, industrial platform, steel frame',
    },
  },

  // 越南语 SEO (主要市场)
  vn: {
    home: {
      title: 'Creston Group | Giải Pháp Tìm Nguồn & Kỹ Thuật Công Nghiệp từ Trung Quốc',
      description: 'Creston Group cung cấp dịch vụ tìm nguồn thiết bị công nghiệp, hỗ trợ kỹ thuật và logistics từ Trung Quốc. Đối tác đáng tin cậy với giá cả cạnh tranh.',
      keywords: 'tìm nguồn Trung Quốc, thiết bị công nghiệp, giải pháp kỹ thuật, sản xuất Trung Quốc, Creston Group',
    },
    about: {
      title: 'Về Chúng Tôi | Creston Group - Liên Doanh Trung-Mỹ',
      description: 'Tìm hiểu về Creston Group, liên doanh Trung-Mỹ tại Thượng Hải chuyên về giải pháp kỹ thuật và dịch vụ tìm nguồn công nghiệp.',
      keywords: 'Creston Group, về chúng tôi, công ty Thượng Hải, liên doanh Trung-Mỹ, tìm nguồn công nghiệp',
    },
    contact: {
      title: 'Liên Hệ | Creston Group - Liên Hệ Với Chúng Tôi',
      description: 'Liên hệ Creston Group để được tư vấn về tìm nguồn công nghiệp. Đội ngũ của chúng tôi tại Thượng Hải sẵn sàng hỗ trợ.',
      keywords: 'liên hệ Creston, tư vấn tìm nguồn, liên hệ Thượng Hải, nhà cung cấp thiết bị',
    },
    coreOffers: {
      title: 'Dịch Vụ | Tìm Nguồn, Kỹ Thuật & Logistics | Creston Group',
      description: 'Khám phá dịch vụ Creston Group: tìm nguồn thiết bị từ Trung Quốc, hỗ trợ kỹ thuật, dịch vụ hậu mãi và logistics.',
      keywords: 'dịch vụ tìm nguồn, hỗ trợ kỹ thuật, logistics Trung Quốc, dịch vụ hậu mãi',
    },
    sourceProduct: {
      title: 'Tìm Nguồn Sản Phẩm từ Trung Quốc | Thiết Bị Công Nghiệp | Creston Group',
      description: 'Tìm nguồn thiết bị công nghiệp từ Trung Quốc: quạt, băng tải, hệ thống lọc bụi, silo. Chất lượng cao, giá cạnh tranh.',
      keywords: 'tìm nguồn Trung Quốc, thiết bị công nghiệp, quạt công nghiệp, băng tải, lọc bụi',
    },
    fans: {
      title: 'Quạt Công Nghiệp từ Trung Quốc | Quạt Ly Tâm, Hướng Trục | Creston Group',
      description: 'Tìm nguồn quạt công nghiệp từ Trung Quốc: quạt ly tâm, quạt hướng trục, quạt thổi. Giá cạnh tranh với hỗ trợ kỹ thuật.',
      keywords: 'quạt công nghiệp Trung Quốc, quạt ly tâm, quạt hướng trục, hệ thống thông gió',
    },
    beltConveyor: {
      title: 'Hệ Thống Băng Tải từ Trung Quốc | Băng Tải Công Nghiệp | Creston Group',
      description: 'Tìm nguồn hệ thống băng tải từ Trung Quốc cho khai thác, sản xuất và logistics. Thiết kế theo yêu cầu.',
      keywords: 'băng tải Trung Quốc, băng tải công nghiệp, vận chuyển vật liệu, băng tải khai thác',
    },
    dustRemoval: {
      title: 'Hệ Thống Lọc Bụi từ Trung Quốc | Thu Bụi Công Nghiệp | Creston Group',
      description: 'Tìm nguồn hệ thống lọc bụi từ Trung Quốc: lọc túi, cyclone, lọc tĩnh điện cho ứng dụng công nghiệp.',
      keywords: 'lọc bụi Trung Quốc, thu bụi công nghiệp, lọc túi, cyclone, kiểm soát ô nhiễm',
    },
    filtration: {
      title: 'Phụ Kiện Lọc từ Trung Quốc | Túi Lọc & Khung Lọc | Creston Group',
      description: 'Tìm nguồn phụ kiện lọc từ Trung Quốc: túi lọc, khung lọc, vật liệu lọc cho hệ thống thu bụi.',
      keywords: 'túi lọc Trung Quốc, khung lọc, vật liệu lọc, phụ tùng thu bụi',
    },
    flowComponents: {
      title: 'Thiết Bị Dòng Chảy từ Trung Quốc | Van & Cửa | Creston Group',
      description: 'Tìm nguồn thiết bị dòng chảy từ Trung Quốc: van quay, cửa trượt, van chuyển hướng cho vận chuyển vật liệu.',
      keywords: 'van quay Trung Quốc, cửa trượt, van chuyển hướng, kiểm soát dòng chảy',
    },
    silosTanks: {
      title: 'Silo & Bồn Chứa từ Trung Quốc | Lưu Trữ Công Nghiệp | Creston Group',
      description: 'Tìm nguồn silo và bồn chứa công nghiệp từ Trung Quốc cho xi măng, ngũ cốc và vật liệu rời.',
      keywords: 'silo thép Trung Quốc, bồn chứa, phễu công nghiệp, lưu trữ vật liệu',
    },
    steelStructures: {
      title: 'Kết Cấu Thép từ Trung Quốc | Gia Công Kim Loại | Creston Group',
      description: 'Tìm nguồn kết cấu thép từ Trung Quốc: khung, sàn, cầu thang và gia công kim loại theo yêu cầu.',
      keywords: 'kết cấu thép Trung Quốc, gia công kim loại, sàn công nghiệp, khung thép',
    },
  },

  // 波兰语 SEO
  pl: {
    home: {
      title: 'Creston Group | Rozwiązania Sourcingowe i Inżynieryjne z Chin',
      description: 'Creston Group dostarcza urządzenia przemysłowe, wsparcie inżynieryjne i rozwiązania logistyczne z Chin.',
      keywords: 'sourcing Chiny, urządzenia przemysłowe, rozwiązania inżynieryjne, produkcja Chiny',
    },
    about: {
      title: 'O Nas | Creston Group - Joint Venture Chińsko-Amerykański',
      description: 'Poznaj Creston Group, chińsko-amerykańskie joint venture w Szanghaju.',
      keywords: 'Creston Group, o nas, firma Szanghaj, joint venture',
    },
    contact: {
      title: 'Kontakt | Creston Group - Skontaktuj się z Nami',
      description: 'Skontaktuj się z Creston Group w sprawie sourcingu przemysłowego.',
      keywords: 'kontakt Creston, zapytanie o sourcing, kontakt Szanghaj',
    },
    coreOffers: {
      title: 'Nasze Usługi | Sourcing, Inżynieria & Logistyka | Creston Group',
      description: 'Poznaj usługi Creston Group: sourcing z Chin, wsparcie inżynieryjne i logistyka.',
      keywords: 'usługi sourcingowe, wsparcie inżynieryjne, logistyka Chiny',
    },
    sourceProduct: {
      title: 'Produkty z Chin | Urządzenia Przemysłowe | Creston Group',
      description: 'Sourcing urządzeń przemysłowych z Chin: wentylatory, przenośniki, odpylacze, silosy.',
      keywords: 'produkty z Chin, urządzenia przemysłowe, wentylatory, przenośniki',
    },
    fans: {
      title: 'Wentylatory Przemysłowe z Chin | Creston Group',
      description: 'Sourcing wentylatorów przemysłowych z Chin: odśrodkowe, osiowe, dmuchawy.',
      keywords: 'wentylatory przemysłowe Chiny, wentylator odśrodkowy, wentylator osiowy',
    },
    beltConveyor: {
      title: 'Systemy Przenośników Taśmowych z Chin | Creston Group',
      description: 'Sourcing systemów przenośników taśmowych z Chin dla górnictwa i produkcji.',
      keywords: 'przenośnik taśmowy Chiny, przenośnik przemysłowy, transport materiałów',
    },
    dustRemoval: {
      title: 'Systemy Odpylania z Chin | Creston Group',
      description: 'Sourcing systemów odpylania z Chin: filtry workowe, cyklony, elektrofiltry.',
      keywords: 'odpylanie Chiny, odpylacz przemysłowy, filtr workowy, cyklon',
    },
    filtration: {
      title: 'Akcesoria Filtracyjne z Chin | Creston Group',
      description: 'Sourcing akcesoriów filtracyjnych z Chin: worki filtracyjne, koszyki.',
      keywords: 'worki filtracyjne Chiny, koszyki filtracyjne, media filtracyjne',
    },
    flowComponents: {
      title: 'Komponenty Przepływowe z Chin | Creston Group',
      description: 'Sourcing komponentów przepływowych z Chin: zawory obrotowe, zasuwy.',
      keywords: 'zawór obrotowy Chiny, zasuwa, rozdzielacz, kontrola przepływu',
    },
    silosTanks: {
      title: 'Silosy i Zbiorniki z Chin | Creston Group',
      description: 'Sourcing silosów i zbiorników przemysłowych z Chin.',
      keywords: 'silos stalowy Chiny, zbiornik magazynowy, lej przemysłowy',
    },
    steelStructures: {
      title: 'Konstrukcje Stalowe z Chin | Creston Group',
      description: 'Sourcing konstrukcji stalowych z Chin: ramy, platformy, chodniki.',
      keywords: 'konstrukcje stalowe Chiny, produkcja metalowa, platforma przemysłowa',
    },
  },

  // 意大利语 SEO
  it: {
    home: {
      title: 'Creston Group | Soluzioni di Sourcing e Ingegneria dalla Cina',
      description: 'Creston Group fornisce attrezzature industriali e soluzioni logistiche dalla Cina.',
      keywords: 'sourcing Cina, attrezzature industriali, soluzioni ingegneristiche, produzione Cina',
    },
    about: {
      title: 'Chi Siamo | Creston Group - Joint Venture Sino-Americana',
      description: 'Scopri Creston Group, una joint venture sino-americana a Shanghai.',
      keywords: 'Creston Group, chi siamo, azienda Shanghai, joint venture',
    },
    contact: {
      title: 'Contattaci | Creston Group - Mettiti in Contatto',
      description: 'Contatta Creston Group per richieste di sourcing industriale.',
      keywords: 'contatta Creston, richiesta sourcing, contatto Shanghai',
    },
    coreOffers: {
      title: 'I Nostri Servizi | Sourcing, Ingegneria & Logistica | Creston Group',
      description: 'Scopri i servizi Creston Group: sourcing dalla Cina e supporto ingegneristico.',
      keywords: 'servizi sourcing, supporto ingegneristico, logistica Cina',
    },
    sourceProduct: {
      title: 'Prodotti dalla Cina | Attrezzature Industriali | Creston Group',
      description: 'Sourcing di attrezzature industriali dalla Cina: ventilatori, nastri trasportatori, silos.',
      keywords: 'prodotti dalla Cina, attrezzature industriali, ventilatori, nastri trasportatori',
    },
    fans: {
      title: 'Ventilatori Industriali dalla Cina | Creston Group',
      description: 'Sourcing di ventilatori industriali dalla Cina: centrifughi, assiali, soffianti.',
      keywords: 'ventilatori industriali Cina, ventilatore centrifugo, ventilatore assiale',
    },
    beltConveyor: {
      title: 'Sistemi di Nastri Trasportatori dalla Cina | Creston Group',
      description: 'Sourcing di nastri trasportatori dalla Cina per estrazione e produzione.',
      keywords: 'nastro trasportatore Cina, trasportatore industriale, movimentazione materiali',
    },
    dustRemoval: {
      title: 'Sistemi di Depolverazione dalla Cina | Creston Group',
      description: 'Sourcing di sistemi di depolverazione dalla Cina: filtri a maniche, cicloni.',
      keywords: 'depolverazione Cina, depolveratore industriale, filtro a maniche, ciclone',
    },
    filtration: {
      title: 'Accessori di Filtrazione dalla Cina | Creston Group',
      description: 'Sourcing di accessori di filtrazione dalla Cina: sacchi filtranti, gabbie.',
      keywords: 'sacchi filtranti Cina, gabbie filtranti, mezzi filtranti',
    },
    flowComponents: {
      title: 'Componenti di Flusso dalla Cina | Creston Group',
      description: 'Sourcing di componenti di flusso dalla Cina: valvole rotative, saracinesche.',
      keywords: 'valvola rotativa Cina, saracinesca, deviatore, controllo flusso',
    },
    silosTanks: {
      title: 'Silos e Serbatoi dalla Cina | Creston Group',
      description: 'Sourcing di silos e serbatoi industriali dalla Cina.',
      keywords: 'silos acciaio Cina, serbatoio stoccaggio, tramoggia industriale',
    },
    steelStructures: {
      title: 'Strutture in Acciaio dalla Cina | Creston Group',
      description: 'Sourcing di strutture in acciaio dalla Cina: telai, piattaforme, passerelle.',
      keywords: 'strutture acciaio Cina, fabbricazione metallica, piattaforma industriale',
    },
  },
};

/**
 * 获取当前语言的 SEO 配置
 */
export function getSEO(locale: string, page: keyof LocaleSEO): PageSEO {
  const config = seoConfig[locale] || seoConfig.en;
  return config[page] || seoConfig.en[page];
}

/**
 * 生成 useHead 配置
 */
export function generateHeadConfig(locale: string, page: keyof LocaleSEO) {
  const seo = getSEO(locale, page);
  return {
    title: seo.title,
    meta: [
      { name: 'description', content: seo.description },
      { name: 'keywords', content: seo.keywords },
      { property: 'og:title', content: seo.ogTitle || seo.title },
      { property: 'og:description', content: seo.ogDescription || seo.description },
    ],
  };
}


